/* eslint-disable promise/prefer-await-to-then */
/* eslint-disable promise/always-return */
import { Logger } from '@/shared/logger/logger';

import { getGtagFuncName, getTagDataLayerName } from './tag_variables';
import {
  TGtagConsentParams,
  TGtagConversionParams,
  TGtagEventParams,
  TGtagUserData,
  TGtagUserProperties,
} from './types';

export class TagManagerWrapper {
  protected dataLayerName: string;
  protected gtagFunctionName: string;
  protected scriptPath: string;

  constructor(
    dataLayerName = 'dataLayer',
    gtagFunctionName = 'gtag',
    scriptPath = 'gtag',
  ) {
    this.dataLayerName = getTagDataLayerName(dataLayerName);
    this.gtagFunctionName = getGtagFuncName(gtagFunctionName);
    this.scriptPath = scriptPath;
    (window as any)[this.dataLayerName] =
      (window as any)[this.dataLayerName] || [];
    if (typeof (window as any)[this.gtagFunctionName] !== 'function') {
      (window as any)[this.gtagFunctionName] = (...args: any[]) =>
        (window as any)[this.dataLayerName].push(...args);
    }
  }
  protected isValidId(id: string, regex: RegExp): boolean {
    return regex.test(id);
  }

  protected gtagFunction = (...args: any) =>
    (window as any)[this.gtagFunctionName](...args);

  protected initConsent(): void {
    const consentParams = {
      ad_personalization: 'granted',
      ad_storage: 'granted',
      ad_user_data: 'granted',
      analytics_storage: 'granted',
    };
    this.gtagFunction('consent', 'default', consentParams);
    this.gtagFunction('consent', 'update', {
      ...consentParams,
      wait_for_update: 100,
    });
  }
  protected customDomain?: string;

  protected loadGtagScript(
    id: string,
    googleTagId: string,
    customDomain?: string,
    replaceRegex = false,
    isSgtmUrl = false,
  ): void {
    if (customDomain) {
      this.customDomain = customDomain;
    }

    if (!this.existingGTM(this.customDomain)) {
      const domain = this.customDomain || 'https://www.googletagmanager.com';
      const scriptPath =
        this.customDomain && !isSgtmUrl
          ? this.scriptPath === 'gtag'
            ? 'wtag'
            : 'wtm'
          : this.scriptPath;

      // Use googleTagId if provided and not empty, otherwise fallback to id
      const tagId = googleTagId && googleTagId.trim() !== '' ? googleTagId : id;
      const scriptUrl = `${domain}/${scriptPath}/js?l=${this.dataLayerName}&id=${tagId}`;

      if (replaceRegex && ['gtag', 'wtag'].includes(this.scriptPath)) {
        fetch(scriptUrl)
          .then(response => response.text())
          .then(content => {
            const modifiedContent = content.replaceAll(
              '/g/collect',
              '/w/tollect',
            );
            const scriptElement = document.createElement('script');
            scriptElement.textContent = modifiedContent;
            document.head.append(scriptElement);
          })
          .catch(error => Logger.error('Failed to load script:', error));
      } else {
        const scriptElement = document.createElement('script');
        scriptElement.async = true;
        scriptElement.src = scriptUrl;
        document.head.append(scriptElement);
      }
    }

    const scriptContent = `
      window.${this.dataLayerName} = window.${this.dataLayerName} || [];
      function ${this.gtagFunctionName}(){${this.dataLayerName}.push(arguments);}
      ${['gtag', 'wtag'].includes(this.scriptPath) ? `${this.gtagFunctionName}('js', new Date());` : ''}
      ${['gtm', 'wtm'].includes(this.scriptPath) ? `${this.gtagFunctionName}({ event: 'gtm.js', 'gtm.start': Date.now()})` : ''}
    `;
    const script2 = document.createElement('script');
    script2.innerHTML = scriptContent;
    document.head.append(script2);
  }

  private existingGTM(customDomain?: string): boolean {
    return Array.from(document.querySelectorAll('script')).some(script => {
      try {
        const url = new URL(script?.src || '');
        const domain = customDomain || 'www.googletagmanager.com';
        if (
          url.hostname === domain &&
          url.searchParams.get('id')?.includes('GTM-')
        ) {
          const l = url.searchParams.get('l');
          if (l) this.dataLayerName = l;
          return true;
        }
      } catch {
        return false;
      }
      return false;
    });
  }

  public trackEvent(
    eventName: string,
    send_to: string,
    eventParams: TGtagEventParams,
    _eventCallback?: (status: boolean) => Promise<boolean>,
    ...args: []
  ): void {
    if (send_to && !send_to.includes('undefined')) {
      eventParams['send_to'] = send_to;
      if (this.customDomain) {
        eventParams['transport_url'] = this.customDomain;
      }
    }
    this.gtagFunction('event', eventName, eventParams, ...args);
  }

  public trackConversionWithCallback(
    send_to: string,
    conversionParams: TGtagConversionParams,
    eventCallback?: (status: boolean) => Promise<boolean>,
    ...args: any[]
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const timeoutId = setTimeout(
        () => reject(new Error('Conversion tracking timed out')),
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        200,
      );

      this.gtagFunction(
        'event',
        'conversion',
        {
          send_to,
          ...conversionParams,
          event_callback: () => {
            clearTimeout(timeoutId);
            (window as any)[`${this.dataLayerName}_processed`] = true;
            resolve(eventCallback ? eventCallback(true) : true);
          },
          event_timeout: 50,
        },
        ...args,
      );
    });
  }

  public trackConversion(
    send_to: string,
    conversionParams: TGtagEventParams,
    _eventCallback?: (status: boolean) => Promise<boolean>,
    ...args: any[]
  ): void {
    this.gtagFunction(
      'event',
      'conversion',
      { send_to, ...conversionParams },
      ...args,
    );
  }

  public pushToDataLayer(eventName: string, eventParams: any): void {
    (window as any)[this.dataLayerName].push({
      event: eventName,
      ...eventParams,
    });
  }

  public trackSGTMConversion(
    conversionParams: TGtagEventParams,
    _eventCallback?: (status: boolean) => Promise<boolean>,
    ...args: any[]
  ): void {
    if (this.customDomain) {
      conversionParams['transport_url'] = this.customDomain;
    }

    this.gtagFunction('event', 'conversion', { ...conversionParams }, ...args);
  }

  public setConsent(consentParams: TGtagConsentParams, ...args: []): void {
    this.gtagFunction('consent', 'update', consentParams, ...args);
  }

  public setUserId(userId: string): void {
    this.gtagFunction('set', 'user_id', userId);
  }

  public setUserProperties(userProperties: TGtagUserProperties): void {
    this.gtagFunction('set', 'user_properties', userProperties);
  }

  public setUserData(userData: TGtagUserData): void {
    this.gtagFunction('set', 'user_data', userData);
  }

  protected loadGTMScripts(
    containerId: string,
    serverUrl: string,
    dataLayerName: string,
  ): void {
    // Load GTM script
    const gtmScript = document.createElement('script');
    gtmScript.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      '${serverUrl}/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','${dataLayerName}','${containerId}');
    `;
    document.head.append(gtmScript);

    // Create noscript iframe
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `${serverUrl}/ns.html?id=${containerId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.append(iframe);
    document.body.append(noscript);
  }
}
